<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      {{
        $router.currentRoute.value.name == "UpdatePodcastAdminCampaign"
          ? "Update"
          : "Add"
      }}
      Campaign
    </h2>

    <!-- Mode Switcher - Only show when adding new campaign -->
    <div v-if="!campaignId" class="flex gap-2 mb-4">
      <Button
        type="button"
        :class="[
          'p-button-sm',
          uploadMode === 'single' ? 'p-button-primary' : 'p-button-outlined',
        ]"
        @click="uploadMode = 'single'"
      >
        Single Campaign
      </Button>
      <Button
        type="button"
        :class="[
          'p-button-sm',
          uploadMode === 'bulk' ? 'p-button-primary' : 'p-button-outlined',
        ]"
        @click="uploadMode = 'bulk'"
      >
        Bulk Upload
      </Button>
    </div>

    <form @submit.prevent="submitForm">
      <!-- Bulk Upload Mode -->
      <Card v-if="uploadMode === 'bulk'" class="mb-3">
        <template #title>
          <div class="text-xl">Bulk Upload Shows</div>
        </template>
        <template #content>
          <div class="flex flex-column gap-3">
            <p class="m-0">Upload CSV file with RSS feeds for multiple shows</p>
            <FileUpload
              ref="bulkFileUpload"
              mode="basic"
              accept="text/csv"
              :maxFileSize="1000000"
              @select="onBulkFileSelect"
              customUpload
              :auto="false"
            />
            <small v-if="bulkUploadError" class="p-error">
              {{ bulkUploadError }}
            </small>
            <download-csv :data="sampleBulkCSV">
              <span
                class="text-blue-500 hover:text-blue-300 underline cursor-pointer"
              >
                Download sample CSV file
              </span>
            </download-csv>
            <div v-if="matchedShowIds.length > 0" class="mt-2">
              <p class="font-semibold text-green-600">
                ✓ {{ matchedShowIds.length }} shows matched successfully
              </p>
            </div>
          </div>
        </template>
      </Card>

      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Campaign Information</div>
        </template>
        <template #content>
          <CampaignFormInputs
            :campaign="form"
            @update:campaign="form = $event"
            @calculate-promotions="calculatePromotions"
            :countries="countries"
            :sellers="sellers"
            :shows="shows"
            :hasShowDropdown="uploadMode === 'single'"
            :showCalculateButton="uploadMode === 'single'"
            :submitted="submitted"
            :isBulkMode="uploadMode === 'bulk'"
          />

          <CampaignScheduledChangePanel
            v-if="campaignId"
            :campaign-id="campaignId"
            :current-value="Number(form.promotions_per_interval) || 0"
            :countries="countries"
            :custom-geos-distribution="initialCustomGeosDistribution"
          />
        </template>
      </Card>

      <div class="w-full flex justify-content-end gap-2">
        <Button
          class="flex-1 sm:flex-initial"
          outlined
          label="Cancel"
          @click="router.go(-1)"
        />
        <Button class="flex-1 sm:flex-initial" type="submit" label="Submit" />
      </div>
    </form>
  </div>
</template>

<script setup>
import moment from "moment";
import Card from "primevue/card";
import Button from "primevue/button";
import FileUpload from "primevue/fileupload";
import country from "country-list-js";
import Papa from "papaparse";
import CampaignFormInputs from "./CampaignFormInputs.vue";
import CampaignScheduledChangePanel from "./CampaignScheduledChangePanel.vue";

import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";
import { ref, computed, onMounted, watch } from "vue";
import axios from "axios";
import { API_BASE_URL } from "@/consts";

const router = useRouter();
const store = useStore();
const toast = useToast();
const countries = ref([]);
const submitted = ref(false);
const matchedShowIds = ref([]);
const bulkUploadError = ref("");
const uploadMode = ref("single");
const sampleBulkCSV = ref([
  { rss_feed: "https://example.com/feed1.xml" },
  { rss_feed: "https://example.com/feed2.xml" },
]);
const form = ref({
  campaign_id: "",
  show_id: "",
  start_date: new Date(moment()),
  end_date: new Date(moment().add(1, "year")),
  geos_wl: ["us"],
  geo_bl: [],
  sellers_wl: [],
  sellers_bl: [],
  promotions_per_interval: 0,
  ctr: 0.8,
  publisher_id: null,
  status: "active",
  campaign_type: "spotify",
  growth_percentage: 5,
  custom_geos_distribution: null,
  apple_pct: null,
});
const initialCustomGeosDistribution = ref(null);

const campaignId = router.currentRoute.value.params.campaignId;
const is_duplicate = router.currentRoute.value.query.is_duplicate;
const token = localStorage.getItem("podcastAdminToken");
const shows = ref([]);

const fetchShows = async () => {
  await store.dispatch("loadShows", {
    token,
    fields: ["show_id", "show_title", "rss_url", "avg_per_month"],
  });
  shows.value = (store.state.shows || []).filter(
    (item) => item.show_title && item.show_title != ""
  );
};

const sellers = computed(() => store.getters.sellers || []);

const fetchSellers = () => {
  if (!store.getters.sellers || store.getters.sellers.length == 0) {
    store.dispatch("loadSellers", { token });
  }
};

const isRequiredFieldsValid = computed(() => {
  const hasCtr =
    form.value.ctr ||
    (form.value.ctrPolicy?.type === "random" &&
      form.value.ctrPolicy?.min != null &&
      form.value.ctrPolicy?.max != null);

  const hasInterval =
    form.value.promotions_per_interval ||
    (form.value.intervalPolicy?.type === "random" &&
      form.value.intervalPolicy?.min != null &&
      form.value.intervalPolicy?.max != null);

  return (
    hasCtr &&
    hasInterval &&
    form.value.start_date &&
    form.value.end_date &&
    (form.value.campaign_type !== "publisher" || form.value.publisher_id)
  );
});

const getCampaignData = async () => {
  try {
    store.commit("setLoading", true);
    const response = await axios.get(
      API_BASE_URL + "/campaigns/" + campaignId,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data.success) {
      const {
        campaign_id,
        show_id,
        start_date,
        end_date,
        geos_wl,
        geo_bl,
        sellers_wl,
        sellers_bl,
        promotions_per_interval,
        ctr,
        publisher_id,
        status,
        campaign_type,
        growth_percentage,
        custom_geos_distribution,
        apple_pct,
      } = response.data.campaign;

      form.value = {
        campaign_id,
        show_id,
        start_date: new Date(start_date),
        end_date: new Date(end_date),
        geos_wl: geos_wl
          ?.toLowerCase()
          .split(",")
          .filter((item) => item != ""),
        geo_bl: geo_bl
          ?.toLowerCase()
          .split(",")
          .filter((item) => item != ""),
        sellers_wl: sellers_wl?.split(",").filter((item) => item != ""),
        sellers_bl: sellers_bl?.split(",").filter((item) => item != ""),
        promotions_per_interval,
        ctr,
        publisher_id: publisher_id || null,
        status,
        campaign_type,
        growth_percentage: growth_percentage || 5,
        custom_geos_distribution: custom_geos_distribution || null,
        apple_pct: apple_pct ?? null,
      };
      initialCustomGeosDistribution.value = custom_geos_distribution || null;

      if (is_duplicate) {
        const now = new Date();
        if (form.value.start_date < now) {
          const periodLength = form.value.end_date - form.value.start_date;
          form.value.start_date = now;
          form.value.end_date = new Date(now.getTime() + periodLength);
        }
      }
    } else
      toast.add({
        severity: "error",
        summary: "Campaign data not found",
        life: 4000,
      });
  } catch (error) {
    console.error(error);
    toast.add({
      severity: "error",
      summary: "Something went wrong...",
      life: 4000,
    });
  }
  store.commit("setLoading", false);
};

const onBulkFileSelect = async (event) => {
  bulkUploadError.value = "";
  matchedShowIds.value = [];

  const file = event.files[0];
  if (!file || file.type !== "text/csv") {
    bulkUploadError.value = "Please select a valid CSV file";
    return;
  }

  try {
    const results = await new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: resolve,
        error: reject,
      });
    });

    if (!results.data || results.data.length === 0) {
      bulkUploadError.value = "CSV file is empty";
      return;
    }

    const firstRow = results.data[0];
    if (!("rss_feed" in firstRow)) {
      bulkUploadError.value = "Missing required column: rss_feed";
      return;
    }

    const rssFeeds = results.data
      .filter((row) => row.rss_feed && row.rss_feed.trim())
      .map((row) => row.rss_feed.trim());

    if (rssFeeds.length === 0) {
      bulkUploadError.value = "No valid RSS feeds found in CSV";
      return;
    }

    const allShows = shows.value || [];
    const matched = rssFeeds
      .map((rssFeed) => allShows.find((s) => s.rss_url === rssFeed)?.show_id)
      .filter(Boolean);

    if (matched.length === 0) {
      bulkUploadError.value =
        "No matching shows found for the provided RSS feeds";
      return;
    }

    matchedShowIds.value = matched;

    toast.add({
      severity: "success",
      summary: "Success",
      detail: `${matched.length} shows matched successfully`,
      life: 3000,
    });
  } catch (error) {
    console.error("Error parsing CSV:", error);
    bulkUploadError.value = "Error parsing CSV file";
  }
};

const calculatePromotions = () => {
  if (!form.value.show_id) {
    toast.add({
      severity: "warn",
      summary: "Warning",
      detail: "Please select a show first.",
      life: 4000,
    });
    return;
  }

  const selectedShow = shows.value.find(
    (show) => show.show_id === form.value.show_id
  );

  if (
    !selectedShow ||
    !selectedShow.avg_per_month ||
    isNaN(selectedShow.avg_per_month)
  ) {
    toast.add({
      severity: "warn",
      summary: "Warning",
      detail:
        "Selected show does not have a valid average listeners per month value.",
      life: 4000,
    });
    return;
  }

  const avgPerMonth = parseFloat(selectedShow.avg_per_month);
  const listenersPerHour = avgPerMonth / 30 / 24;
  const promotionsPerInterval =
    listenersPerHour * ((form.value.growth_percentage || 5) / 100);

  form.value.promotions_per_interval = promotionsPerInterval.toFixed(2);
};

const buildCampaignRequestBody = (additionalFields = {}) => {
  const payload = {
    show_id: form.value.show_id,
    start_date: form.value.start_date.toISOString(),
    end_date: form.value.end_date.toISOString(),
    geos_wl: form.value.geos_wl?.join() || "",
    geo_bl: form.value.geo_bl?.join() || "",
    sellers_wl: form.value.sellers_wl?.join() || "",
    sellers_bl: form.value.sellers_bl?.join() || "",
    status: form.value.status,
    promotions_per_interval: form.value.promotions_per_interval,
    ctr: form.value.ctr,
    campaign_type: form.value.campaign_type,
    publisher_id: form.value.publisher_id || "",
    custom_geos_distribution: form.value.custom_geos_distribution || null,
    apple_pct: form.value.apple_pct ?? null,
    ...additionalFields,
  };

  if (form.value.campaign_id && !is_duplicate) {
    payload.campaign_id = form.value.campaign_id;
  }

  if (form.value.ctrPolicy) {
    payload.ctrPolicy = form.value.ctrPolicy;
  }
  if (form.value.intervalPolicy) {
    payload.intervalPolicy = form.value.intervalPolicy;
  }

  return payload;
};

const submitCampaignRequest = async (payload) => {
  try {
    store.commit("setLoading", true);
    const response = await axios.post(
      API_BASE_URL + "/campaigns/add",
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (response.data?.success) {
      submitted.value = false;
      const successMessage =
        uploadMode.value === "bulk"
          ? `${matchedShowIds.value.length} campaigns added successfully!`
          : `Campaign ${campaignId ? "updated" : "added"} successfully!`;

      toast.add({
        severity: "success",
        summary: "Success",
        detail: successMessage,
        life: 4000,
      });
      store.dispatch("loadCampaigns", { token });
      router.replace({ name: "PodcastAdminCampaignsList" });
    } else {
      toast.add({
        severity: "error",
        detail: "Something went wrong...",
        life: 4000,
      });
    }
  } catch (error) {
    toast.add({
      severity: "error",
      detail: error.response?.data?.message || "Something went wrong...",
      life: 4000,
    });
    console.error("Campaign submission error:", error);
  } finally {
    store.commit("setLoading", false);
  }
};

const submitForm = async () => {
  submitted.value = true;

  // Date validation
  if (form.value.start_date >= form.value.end_date) {
    toast.add({
      severity: "warn",
      summary: "Warning",
      detail: "End date must be later than start date!",
      life: 4000,
    });
    return;
  }

  if (form.value.custom_geos_distribution) {
    const total = form.value.custom_geos_distribution.reduce(
      (sum, row) => sum + (row.pct || 0),
      0
    );
    const allRowsHavePct = form.value.custom_geos_distribution.every(
      (row) => row.pct != null
    );
    if (!allRowsHavePct || total !== 100) {
      toast.add({
        severity: "warn",
        summary: "Warning",
        detail:
          "Custom geo distribution: give every whitelisted country a percentage, adding up to 100%",
        life: 4000,
      });
      return;
    }
  }

  // Bulk mode validation and submission
  if (uploadMode.value === "bulk") {
    if (matchedShowIds.value.length === 0) {
      toast.add({
        severity: "error",
        summary: "Validation Error",
        detail: "Please upload a CSV file with valid RSS feeds",
        life: 4000,
      });
      return;
    }

    if (!isRequiredFieldsValid.value) {
      toast.add({
        severity: "warn",
        summary: "Warning",
        detail: "Fill in all required campaign fields!",
        life: 4000,
      });
      return;
    }

    await submitCampaignRequest(
      buildCampaignRequestBody({
        show_ids: matchedShowIds.value,
        is_bulk: true,
      })
    );
    return;
  }

  // Single mode validation and submission
  if (!form.value.show_id || !isRequiredFieldsValid.value) {
    toast.add({
      severity: "warn",
      summary: "Warning",
      detail: "Fill in the blanks, please!",
      life: 4000,
    });
    return;
  }

  await submitCampaignRequest(
    buildCampaignRequestBody({
      is_duplicate,
    })
  );
};

// Watch for changes in growth_percentage and recalculate
watch(
  () => form.value.growth_percentage,
  (newValue, oldValue) => {
    if (newValue !== oldValue && form.value.show_id && !campaignId) {
      calculatePromotions();
    }
  }
);

// Watch for changes in show_id and recalculate if growth_percentage exists
watch(
  () => form.value.show_id,
  (newValue, oldValue) => {
    if (
      newValue &&
      newValue !== oldValue &&
      form.value.growth_percentage &&
      !campaignId
    ) {
      calculatePromotions();
    }
  }
);

// Watch for changes in status to set default values for "Mellow mode"
watch(
  () => form.value.status,
  (newStatus) => {
    if (newStatus == "Mellow mode") {
      form.value.promotions_per_interval = 0;
      form.value.ctr = 0.18;
      form.value.growth_percentage = 5;
    }
  }
);

onMounted(async () => {
  countries.value = country.names().map((name) => {
    const data = country.findByName(name);
    return {
      name,
      geo: data.code.iso2.toLowerCase(),
    };
  });
  fetchShows();
  fetchSellers();
  if (campaignId) {
    form.value.campaign_id = campaignId;
    getCampaignData();
  }
});
</script>

<style scoped>
.p-inputnumber,
.p-inputtext,
.p-inputgroup {
  width: 100%;
}

label {
  display: block;
  margin-bottom: 0.5rem;
}

/* Ensure inputs don't overflow their containers */
input {
  max-width: 100%;
}
</style>
